/**
 * DOMAINS & ENDPOINTS CONFIGURATION
 *
 * PURPOSE: Centralized configuration for external service domains and endpoints
 *
 * WHAT THIS CONTAINS:
 * - AWS S3 domain configuration
 * - Firebase service domains
 * - External API endpoints
 * - CDN and storage service URLs
 */

import { config } from './config';
import type { FirebaseConfig } from './config.interface';

/**
 * AWS S3 DOMAINS - For file uploads and storage
 *
 * Dynamically builds S3 domains based on environment configuration
 * Used for: File uploads, image storage, CDN access
 */
export const getS3Domains = (): string[] => {
  const s3Domains: string[] = [];

  // Add S3 domains from environment variables
  const s3BucketName = process.env.REACT_APP_S3_BUCKET_NAME;
  const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';

  if (s3BucketName) {
    s3Domains.push(
      `https://${s3BucketName}.s3.amazonaws.com`, // Legacy format
      `https://${s3BucketName}.s3.${awsRegion}.amazonaws.com`, // Regional format
      `https://s3.${awsRegion}.amazonaws.com`, // General S3 API
      `https://s3.amazonaws.com`, // Global S3 endpoint
    );
  } else {
    // Fallback - allow common S3 patterns (only valid wildcard patterns)
    s3Domains.push(
      'https://*.s3.eu-west-2.amazonaws.com', // Valid: single wildcard for bucket name
      'https://s3.amazonaws.com', // Valid: specific domain
    );
  }

  return s3Domains;
};

/**
 * FIREBASE DOMAINS - For authentication and analytics
 *
 * Dynamically builds Firebase-related domains from the current Firebase config
 * This ensures CSP stays in sync with your actual Firebase project configuration
 * Used for: Authentication, analytics, OAuth flows
 */
export const getFirebaseDomains = (): string[] => {
  const firebaseConfig: FirebaseConfig = config.OicdAuthenticatorConfig;
  const domains: string[] = [
    // Core Firebase Auth and API endpoints
    'https://identitytoolkit.googleapis.com', // Firebase Auth API
    'https://securetoken.googleapis.com', // Firebase Auth tokens
    'https://firebase.googleapis.com', // Firebase Analytics config
    'https://firebaseinstallations.googleapis.com', // Firebase Installations API
    'https://www.googleapis.com', // Google APIs
    'https://accounts.google.com', // Google Sign-In
    'https://oauth.googleusercontent.com', // OAuth redirects

    // 🔥 CRITICAL: Additional Google auth domains for popup authentication
    'https://apis.google.com', // Google APIs (authentication)
    'https://www.google.com', // Google Sign-In button resources
    'https://ssl.gstatic.com', // Google static resources
    'https://www.gstatic.com', // Google static content
    'https://gstatic.com', // Google static content (alternative)
    'https://fonts.googleapis.com', // Google Fonts for auth UI
    'https://fonts.gstatic.com', // Google Fonts static files

    // Google Analytics domains
    'https://www.google-analytics.com', // Google Analytics
    'https://region1.google-analytics.com', // Google Analytics regional endpoint
    'https://analytics.google.com', // Google Analytics
  ];

  // Add domain-specific endpoints from your Firebase config
  if (firebaseConfig?.authDomain) {
    domains.push(`https://${firebaseConfig.authDomain}`);
  }

  // Add any additional Firebase project-specific domains
  if (firebaseConfig?.projectId) {
    domains.push(`https://${firebaseConfig.projectId}.firebaseapp.com`);
    domains.push(`https://${firebaseConfig.projectId}.web.app`);
  }

  return domains;
};

/**
 * ALL EXTERNAL DOMAINS - Combined list for convenience
 *
 * Combines all external service domains into a single list
 * Used for: CSP headers, security configurations, allowlists
 */
export const getAllExternalDomains = (): string[] => {
  return [...getS3Domains(), ...getFirebaseDomains()];
};
